// Brand layer first: tokens define the palette and scale, motion the durations and
// keyframes, and app.css builds element rules and page grammar on top of both.
import "./styles/brand/tokens.css";
import "./styles/brand/motion.css";
import "./app.css";
import { mount } from "svelte";
import App from "./App.svelte";
import { router } from "./router.svelte";
import { session, wireAuthHandlers } from "$lib/stores/session.svelte";
import { installTelemetryLifecycle } from "$lib/telemetry/emit";
import { installReleaseDetection } from "$lib/release/detection";
import { setReleaseHoldProvider } from "$lib/release/holds";
import { updates } from "$lib/release/updates.svelte";
import { canUseMockApi, retireMockServiceWorkers } from "./mocks/runtime";

async function boot(): Promise<void> {
  // Mock transport is valid only in the explicit local mock mode. A production
  // deployment must retire any worker left by an accidentally deployed mock build.
  if (canUseMockApi(import.meta.env.MODE, import.meta.env.VITE_API_MOCK, window.location.hostname)) {
    const { startMockServiceWorker } = await import("./mocks/browser");
    await startMockServiceWorker();
  } else if ("serviceWorker" in navigator) {
    const retired = await retireMockServiceWorkers(navigator.serviceWorker).catch(() => ({
      controlled: false,
      unregistered: 0
    }));
    if (retired.controlled && retired.unregistered > 0) {
      window.location.reload();
      return;
    }
  }

  wireAuthHandlers(() => router.navigate("/login", { replace: true }));
  setReleaseHoldProvider((reason) => updates.hold(reason));
  installTelemetryLifecycle();
  installReleaseDetection();
  router.init();
  // Probe the session cookie; App renders a splash until this settles.
  void session.bootstrap();

  const target = document.getElementById("app");
  if (!target) throw new Error("Missing #app mount point");
  mount(App, { target });
}

void boot();
